/**
 * Analytics Service
 *
 * Unified analytics service for tracking events across PostHog and Google Analytics.
 * This service is used in non-React contexts (services, stores).
 * For React components, use the useAnalytics hook instead.
 *
 * Event routing:
 * - Funnel events → Both GA4 and PostHog (critical for ads optimization)
 * - Product events → PostHog only (would clutter GA4)
 * - Error events → PostHog only (with session replay context)
 */

import posthog from 'posthog-js'
import {
  ANALYTICS_EVENTS,
  PRODUCT_EVENTS,
  ERROR_EVENTS,
  type ElementType,
  type CanvasAction,
  type SaveMethod,
} from '../constants/analytics'
import { trackBeginCheckout } from '../utils/dataLayer'

// Type for GA4 gtag function
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    dataLayer?: unknown[]
  }
}

type EventProps = Record<string, string | number | boolean | undefined>

const GOOGLE_ADS_TRACKING_ID = 'AW-17815380048'
const GTAG_LOADER_ID = 'pb-gtag-loader'
const configuredIds = new Set<string>()
let gtagBootstrapReady = false

const getSafePageLocation = () =>
  `${window.location.origin}${window.location.pathname}`

const ensureGtagBootstrap = (): void => {
  if (gtagBootstrapReady || typeof window === 'undefined') return

  window.dataLayer = window.dataLayer || []
  if (typeof window.gtag !== 'function') {
    window.gtag = (...args: unknown[]) => {
      window.dataLayer?.push(args)
    }
  }

  window.gtag('js', new Date())
  gtagBootstrapReady = true
}

const ensureGtagLoader = (googleTagId: string): void => {
  if (typeof document === 'undefined') return

  // If GTM is already installed from index.html, it owns script loading.
  // Injecting gtag.js again can duplicate config/event processing in debug mode.
  if (
    typeof window !== 'undefined' &&
    (typeof (window as Window & { google_tag_manager?: unknown })
      .google_tag_manager !== 'undefined' ||
      !!document.querySelector('script[src*="googletagmanager.com/gtm.js"]'))
  ) {
    return
  }

  const existingLoader = document.getElementById(
    GTAG_LOADER_ID
  ) as HTMLScriptElement | null
  if (existingLoader) return

  const script = document.createElement('script')
  script.id = GTAG_LOADER_ID
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${googleTagId}`
  document.head.appendChild(script)
}

const configureTrackingId = (id: string): void => {
  if (typeof window.gtag !== 'function' || configuredIds.has(id)) return

  window.gtag('config', id)
  configuredIds.add(id)
}

/**
 * Check if PostHog is initialized
 */
const isPostHogReady = (): boolean => {
  try {
    return posthog.__loaded === true
  } catch {
    return false
  }
}

/**
 * Send event to Google Analytics 4
 */
const sendToGA4 = (eventName: string, props?: EventProps): void => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, props)
  } else if (import.meta.env.MODE === 'development') {
    // eslint-disable-next-line no-console
    console.debug(`[Analytics GA4] ${eventName}`, props)
  }
}

/**
 * Send event to PostHog
 */
const sendToPostHog = (eventName: string, props?: EventProps): void => {
  if (isPostHogReady()) {
    posthog.capture(eventName, props)
  } else if (import.meta.env.MODE === 'development') {
    // eslint-disable-next-line no-console
    console.debug(`[Analytics PostHog] ${eventName}`, props)
  }
}

/**
 * Analytics Service
 * Use this in non-React contexts (services, stores, utilities)
 */
export const AnalyticsService = {
  ensureGtagLoaded: (trackingId?: string): void => {
    if (typeof window === 'undefined') return

    const googleTagId = trackingId || GOOGLE_ADS_TRACKING_ID
    if (!googleTagId) return

    ensureGtagBootstrap()
    ensureGtagLoader(googleTagId)

    if (trackingId) {
      configureTrackingId(trackingId)
    }
    if (GOOGLE_ADS_TRACKING_ID) {
      configureTrackingId(GOOGLE_ADS_TRACKING_ID)
    }
  },

  configureTrackingId,

  trackPageView: (trackingId?: string, pathname?: string): void => {
    if (!trackingId || typeof window.gtag !== 'function') return

    window.gtag('config', trackingId, {
      page_title: document.title,
      page_location: getSafePageLocation(),
      page_path: pathname || window.location.pathname,
    })
  },

  // ============================================================
  // CORE FUNNEL EVENTS (sent to BOTH GA4 and PostHog)
  // ============================================================

  /**
   * Track core funnel event to both GA4 and PostHog
   */
  trackFunnel: (eventName: string, props?: EventProps): void => {
    sendToPostHog(eventName, props)
    sendToGA4(eventName, props)
  },

  /**
   * User creates a new board (top of funnel)
   */
  newBoard: (templateId: string): void => {
    AnalyticsService.trackFunnel(ANALYTICS_EVENTS.NEW_BOARD, {
      template_id: templateId,
    })
  },

  /**
   * User returns to edit an existing board (re-engagement)
   */
  editBoard: (boardId: string, elementCount: number): void => {
    AnalyticsService.trackFunnel(ANALYTICS_EVENTS.EDIT_BOARD, {
      board_id: boardId,
      element_count: elementCount,
    })
  },

  /**
   * User adds an element to the canvas
   */
  elementAdded: (type: ElementType): void => {
    AnalyticsService.trackFunnel(ANALYTICS_EVENTS.ELEMENT_ADDED, { type })
  },

  /**
   * User sends a message to AI chat
   */
  chatSent: (messageLength: number): void => {
    AnalyticsService.trackFunnel(ANALYTICS_EVENTS.CHAT_SENT, {
      message_length: messageLength,
    })
  },

  /**
   * User performs a canvas action
   */
  canvasAction: (action: CanvasAction): void => {
    AnalyticsService.trackFunnel(ANALYTICS_EVENTS.CANVAS_ACTION, { action })
  },

  /**
   * User opens checkout/purchase flow (high intent)
   */
  purchaseStarted: (price: number, size: string): void => {
    trackBeginCheckout({
      value: price,
      size,
    })
    AnalyticsService.trackFunnel(ANALYTICS_EVENTS.PURCHASE_STARTED, {
      est_price: price,
      print_size: size,
    })
  },

  /**
   * User completes a purchase (conversion)
   * Note: This may be called from Shopify webhook in the future
   */
  purchase: (
    transactionId: string,
    value: number,
    currency: string = 'USD'
  ): void => {
    AnalyticsService.trackFunnel(ANALYTICS_EVENTS.PURCHASE, {
      transaction_id: transactionId,
      value,
      currency,
    })
  },

  // ============================================================
  // PRODUCT EVENTS (sent to PostHog only)
  // ============================================================

  /**
   * Track product intelligence event to PostHog only
   */
  trackProduct: (eventName: string, props?: EventProps): void => {
    sendToPostHog(eventName, props)
  },

  /**
   * Generic track method for ad-hoc events (PostHog only)
   * Used for debugging/investigation events that don't need GA4
   */
  track: (eventName: string, props?: EventProps): void => {
    sendToPostHog(eventName, props)
  },

  /**
   * Board saved (manual or auto)
   * #5 Enhanced with element_count for save event forensics
   */
  boardSaved: (method: SaveMethod, elementCount?: number): void => {
    AnalyticsService.trackProduct(PRODUCT_EVENTS.BOARD_SAVED, {
      method,
      element_count: elementCount,
    })
  },

  /**
   * Board title renamed
   */
  boardRenamed: (): void => {
    AnalyticsService.trackProduct(PRODUCT_EVENTS.BOARD_RENAMED)
  },

  /**
   * Feature interaction for usage heatmaps
   */
  featureUsed: (featureName: string): void => {
    AnalyticsService.trackProduct(PRODUCT_EVENTS.FEATURE_USED, {
      feature_name: featureName,
    })
  },

  /**
   * User viewed guides panel
   */
  guidesViewed: (guideId?: string): void => {
    AnalyticsService.trackProduct(PRODUCT_EVENTS.GUIDES_VIEWED, {
      guide_id: guideId,
    })
  },

  /**
   * Modal opened
   */
  modalOpened: (modalName: string): void => {
    AnalyticsService.trackProduct(PRODUCT_EVENTS.MODAL_OPENED, {
      modal_name: modalName,
    })
  },

  /**
   * Modal closed
   */
  modalClosed: (modalName: string, action?: string): void => {
    AnalyticsService.trackProduct(PRODUCT_EVENTS.MODAL_CLOSED, {
      modal_name: modalName,
      action,
    })
  },

  // ============================================================
  // ERROR EVENTS (sent to PostHog only)
  // ============================================================

  /**
   * Track error event to PostHog only
   */
  trackError: (eventName: string, props?: EventProps): void => {
    sendToPostHog(eventName, props)
  },

  /**
   * Print validation failed
   */
  printValidationFailed: (reason: string): void => {
    AnalyticsService.trackError(ERROR_EVENTS.PRINT_VALIDATION_FAILED, {
      reason,
    })
  },

  /**
   * Checkout/purchase error
   */
  purchaseError: (step: string, error: string): void => {
    AnalyticsService.trackError(ERROR_EVENTS.PURCHASE_ERROR, {
      step,
      error,
    })
  },

  /**
   * AI generation or response error
   */
  aiError: (type: string, message?: string): void => {
    AnalyticsService.trackError(ERROR_EVENTS.AI_ERROR, {
      type,
      message,
    })
  },

  /**
   * Canvas rendering or interaction error
   */
  canvasError: (error: string, component?: string): void => {
    AnalyticsService.trackError(ERROR_EVENTS.CANVAS_ERROR, {
      error,
      component,
    })
  },
}

export default AnalyticsService
